import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {FormGroup, Tooltip, TextInput} from '@packages/systems/spring';
import {EmailNotificationFieldLabel} from './emailNotificationFieldLabel';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {collectFieldsInsideForm} from '@packages/utilities/platform';
import {FormField} from '@packages/systems/flows/types';
import {validateReplyToTokens} from './utilities';

type EmailReplyToFieldProps = {
  emailReplyTo: string;
  setEmailReplyTo: (replyToString: string) => void;
  formFieldTokenOptions: {label: string; value: string; type: string}[];
  setFormFieldTokenOptions: Dispatch<
    SetStateAction<{label: string; value: string; type: string}[]>
  >;
  formFormElement: ExpressionElement;
  placeholder?: string;
};

export const EmailReplyToField = ({
  emailReplyTo,
  setEmailReplyTo,
  formFieldTokenOptions,
  setFormFieldTokenOptions,
  formFormElement,
  placeholder,
}: EmailReplyToFieldProps) => {
  const [replyToError, setReplyToError] = useState(false);
  const [replyToErrorMessage, setReplyToErrorMessage] = useState('');

  // Set the "+ Add Fields" token options
  useEffect(() => {
    const fields = collectFieldsInsideForm(formFormElement, {
      password: false,
      recaptcha: false,
    })?.fields;

    const textAndEmailFields = fields.filter(
      (field: FormField) =>
        field.fieldType.tag === 'Plain' || field.fieldType.tag === 'Email'
    );
    const formattedFieldOptions = textAndEmailFields.map(
      ({fieldType, fieldName}) => ({
        label: fieldName,
        value:
          fieldType.tag === 'Email'
            ? `< {{ ${fieldName} }} >`
            : `{{ ${fieldName} }}`,
        type: fieldType.tag === 'Email' ? 'email' : 'text',
      })
    );

    setFormFieldTokenOptions(formattedFieldOptions);
  }, [formFormElement, setFormFieldTokenOptions]);

  const catchReplyToError = useCallback(
    (email: string) => {
      const {isValid, errorMessage} = validateReplyToTokens(
        email,
        formFieldTokenOptions
      );

      if (!isValid) {
        setReplyToError(true);
        setReplyToErrorMessage(errorMessage);
      }
    },
    [formFieldTokenOptions]
  );

  const validateReplyTo = useCallback(() => {
    setReplyToError(false);
    setReplyToErrorMessage('');
    emailReplyTo.split(',').forEach(catchReplyToError);
  }, [emailReplyTo, catchReplyToError]);

  useEffect(() => {
    validateReplyTo();
  }, [emailReplyTo, validateReplyTo]);

  return (
    <FormGroup isError={replyToError} id="email-notification-reply-to">
      <EmailNotificationFieldLabel
        htmlFor="email-notification-reply-to"
        text="Reply To"
        helpText="Enter the email address of the person who will receive the reply to the email notifications."
        tokens={formFieldTokenOptions}
        onTokenClick={(value: string) => {
          setEmailReplyTo(emailReplyTo + value);
        }}
      />
      <Tooltip
        label={replyToErrorMessage}
        isOpen={replyToError}
        colorVariant="danger"
        placement="top"
      >
        <TextInput
          value={emailReplyTo}
          placeholder={placeholder}
          onChange={(e) => {
            setEmailReplyTo(e.target.value);
          }}
          onBlur={validateReplyTo}
        ></TextInput>
      </Tooltip>
    </FormGroup>
  );
};
